import { Formik } from "formik";
import i18next from "i18next";
import React, { useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import * as Yup from "yup";

import { Box, Button, Spacing, Stack, Text } from "@noom/wax-component-library";

import { InputWithSuffix } from "@/components/form";
import { enforcePositiveInt } from "@/components/form/utils";
import {
  QuestionComponentProps,
  QuestionDefinition,
} from "@/components/survey/Question";
import {
  calculateBMIImperial,
  calculateImperialWeightFromBMI,
} from "@/utils/bmi";
import { calculateRecommendedWeightRange } from "@/utils/calculateGoals";
import { captureException } from "@/utils/sentry";

import { BaseQuestion } from "../core";

const QUESTION_ID = "idealWeight";

declare module "@/contexts" {
  interface SurveyAnswers {
    idealWeight?: number;
  }
}

type IdealWeightFormValues = {
  idealWeight?: number;
};

const IdealWeightSchema = Yup.object({
  idealWeight: Yup.number()
    .required(() => i18next.t("form.idealWeight.errors.required"))
    .positive(() => i18next.t("form.idealWeight.errors.invalid")),
});

const IdealWeightQuestion: React.FC<QuestionComponentProps> = ({
  onClickBack,
  onClickNext,
  surveyAnswers,
}) => {
  const [shouldShowConfirmation, setShouldShowConfirmation] = useState(false);
  const { t } = useTranslation();

  const {
    heightWeight: { heightFeetPortion, heightInchesPortion, weight } = {},
  } = surveyAnswers;

  const initialValues: IdealWeightFormValues = {
    idealWeight: surveyAnswers.idealWeight,
  };

  const { lowerRangeRecommendedWeight, upperRangeRecommendedWeight } =
    calculateRecommendedWeightRange(
      heightFeetPortion,
      heightInchesPortion,
      weight,
    );

  const getDescriptionText = (idealWeight: number, currentWeight: number) => {
    if (idealWeight > currentWeight) {
      return <Text>{t("idealWeight.resultIdealWeightHigher")}</Text>;
    }

    if (shouldShowConfirmation) {
      if (
        heightFeetPortion === undefined ||
        heightInchesPortion === undefined ||
        weight === undefined
      ) {
        captureException(
          Error("Missing height when calculating minimum ideal weight"),
        );
        return null;
      }

      const weightFromMinBMI = Math.ceil(
        calculateImperialWeightFromBMI(
          heightFeetPortion,
          heightInchesPortion,
          18.5,
        ),
      );

      // If the weight from the minimum BMI is higher than the current weight,
      // we don't want to suggest the user update to that weight because it
      // would be gaining weight, so we would suggest their current weight - 1.
      const minWeight =
        weightFromMinBMI < weight ? weightFromMinBMI : weight - 1;

      return (
        <Text>
          <Trans
            components={{
              boldText: <Text fontWeight={500} />,
            }}
            i18nKey="idealWeight.liabilityWaiverConfirmation"
            values={{ minWeight }}
          />
        </Text>
      );
    }

    return null;
  };

  const handleAnswerBeforeProceeding = (idealWeight?: number) => {
    if (
      heightFeetPortion === undefined ||
      heightInchesPortion === undefined ||
      idealWeight === undefined
    ) {
      captureException(
        Error(
          "Missing height or ideal weight when continuing on the ideal weight question",
        ),
      );
      return;
    }

    const idealBMI = calculateBMIImperial(
      heightFeetPortion,
      heightInchesPortion,
      idealWeight,
    );

    if (idealBMI < 18.5 && !shouldShowConfirmation) {
      setShouldShowConfirmation(true);
      return;
    }

    onClickNext(idealWeight);
  };

  return (
    <BaseQuestion
      onClickBack={onClickBack}
      questionId={QUESTION_ID}
      questionText={t("idealWeight.questionText")}
    >
      <Formik
        initialValues={initialValues}
        onSubmit={({ idealWeight }: IdealWeightFormValues) =>
          handleAnswerBeforeProceeding(idealWeight)
        }
        validationSchema={IdealWeightSchema}
      >
        {({ errors, getFieldProps, handleSubmit, touched, values }) => (
          <form onSubmit={handleSubmit}>
            <Stack spacing={Spacing[4]}>
              <InputWithSuffix
                {...getFieldProps("idealWeight")}
                autoFocus
                error={touched.idealWeight && errors.idealWeight}
                onKeyDown={enforcePositiveInt}
                suffix="lbs."
                type="number"
              />
              {lowerRangeRecommendedWeight && upperRangeRecommendedWeight && (
                <Box marginTop="var(--spacing-m)" textAlign="center">
                  <Trans
                    components={{
                      boldText: <Text fontWeight={500} />,
                    }}
                    i18nKey="idealWeight.recommendedWeightRange"
                    values={{
                      lowerRangeRecommendedWeight,
                      upperRangeRecommendedWeight,
                    }}
                  />
                </Box>
              )}
              {values.idealWeight &&
                weight &&
                getDescriptionText(values.idealWeight, weight)}
              <Button
                colorScheme="primary"
                isDisabled={
                  !!(values.idealWeight && weight) &&
                  values.idealWeight > weight
                }
                size="xl"
                type="submit"
              >
                {t("common.next")}
              </Button>
            </Stack>
          </form>
        )}
      </Formik>
    </BaseQuestion>
  );
};

export const Q_IDEAL_WEIGHT: QuestionDefinition = {
  id: QUESTION_ID,
  shouldShowQuestion: ({ experiments, selectedProgram }) =>
    experiments["diabetes-program-triage"] === "variant" &&
    selectedProgram !== "MOOD",
  component: IdealWeightQuestion,
};
